import { useState, useEffect } from "react"
import { DateTime } from "luxon"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { DatePicker } from "@mui/x-date-pickers"
import { Grid, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CreateUpdateDayMenuFormValues, DaysOfWeek } from "../../menuPlanningTypes"
import { FormikChip } from "../../../../../../shared/components/forms/Formik"
import { DayMenuAddButton } from "./FormElements"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "2rem",
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        boxShadow: "0px 2px 6px 0px #00000014",
        borderRadius: "0.75rem",
    },
    title: {
        color: theme.palette.tailwind.slate[500],
    },
    dateFilter: {
        maxWidth: "11rem",
    },
}))
export function RecurrenceList() {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateUpdateDayMenuFormValues>()
    const today = DateTime.now()

    const defaultMinDate = values.publishDate && values.publishDate < today ? values.publishDate : today

    const [minDate, setMinDate] = useState<DateTime>(defaultMinDate)

    useEffect(() => {
        if (values.publishDate && !!values.recurrence && values.recurrence?.fromDate !== values.publishDate) {
            setFieldValue("recurrence.fromDate", values.publishDate)
        }
    }, [values.publishDate, setFieldValue, values.recurrence?.fromDate])

    const handleAddRecurrence = () => {
        setFieldValue("recurrence", {
            fromDate: values.publishDate,
        })
    }
    const handleRemoveRecurrence = () => {
        setFieldValue("recurrence", undefined)
    }
    return (
        <Grid container gap={3}>
            <Grid container gap={1.5} alignItems="center">
                <LocalizedStrict id="merchant-menu-planning-dialog-publishDate-date-picker" attrs={{ label: true }}>
                    <DatePicker
                        value={values.publishDate}
                        onChange={(newValue: DateTime | null) => {
                            if (newValue) {
                                setMinDate(today)
                                setFieldValue("publishDate", newValue)
                            }
                        }}
                        label="Publish date"
                        className={classes.dateFilter}
                        onOpen={() => {
                            setMinDate(today)
                        }}
                        onClose={() => {
                            setMinDate(defaultMinDate)
                        }}
                        minDate={minDate}
                    />
                </LocalizedStrict>
                {!values?.recurrence ? (
                    <LocalizedStrict id="merchant-menu-planning-dialog-add-recurrence-button" attrs={{ title: true }}>
                        <DayMenuAddButton title="+ Add recurrence" handleClick={handleAddRecurrence} />
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict
                        id="merchant-menu-planning-dialog-remove-recurrence-button"
                        attrs={{ title: true }}
                    >
                        <DayMenuAddButton title="Remove Recurrence" handleClick={handleRemoveRecurrence} errorType />
                    </LocalizedStrict>
                )}
            </Grid>

            {!!values?.recurrence && (
                <Grid container gap={3} flexDirection="column" className={classes.container}>
                    <LocalizedStrict id="merchant-menu-planning-dialog-recurrence">
                        <Typography variant="body1" fontWeight={600} className={classes.title}>
                            Recurrence
                        </Typography>
                    </LocalizedStrict>
                    <Grid container item gap={1}>
                        {Object.values(DaysOfWeek).map((day) => {
                            return (
                                <Grid key={day} item>
                                    <LocalizedEnum base="merchant-menu-planning-week-day" enumValue={day}>
                                        <FormikChip id={day} name="recurrence.days" value={day}>
                                            {day.toLowerCase()}
                                        </FormikChip>
                                    </LocalizedEnum>
                                </Grid>
                            )
                        })}
                    </Grid>
                    <LocalizedStrict id="merchant-menu-planning-dialog-from-date-picker" attrs={{ label: true }}>
                        <DatePicker
                            value={values.recurrence?.fromDate}
                            onChange={(newValue: DateTime | null) => {
                                if (newValue) {
                                    setFieldValue("recurrence.fromDate", newValue)
                                }
                            }}
                            label="From date"
                            disabled
                            maxDate={values.recurrence?.toDate}
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-menu-planning-dialog-to-date-picker" attrs={{ label: true }}>
                        <DatePicker
                            value={values.recurrence?.toDate}
                            onChange={(newValue: DateTime | null) => {
                                if (newValue) {
                                    setFieldValue("recurrence.toDate", newValue)
                                }
                            }}
                            label="To date"
                            minDate={values.recurrence?.fromDate}
                        />
                    </LocalizedStrict>
                </Grid>
            )}
        </Grid>
    )
}
